import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { PageComponent } from '@next/core-lib/components';
import { FormComponent } from '@next/core-lib/form';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AppState, NutrientCategory } from '../shared/models';
import { getNutrientCategories } from '../store/selectors';
import { LabResults, OverviewState } from './models/overview.model';
import * as OverviewActions from './store/actions/overview.actions';
import { getLabResults } from './store/selectors/overview.selectors';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  imports: [
    PageComponent,
    FormComponent,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatTabGroup,
    MatTab,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class OverviewComponent implements OnInit, OnDestroy {
  private readonly overviewStore = inject(Store<OverviewState>);
  private readonly appStore = inject(Store<AppState>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly localeService = inject(LocaleService);

  form = this.formBuilder.group({
    search: '',
  });

  labResults$ = this.overviewStore.pipe(select(getLabResults));
  nutrientCategories$ = this.appStore.pipe(select(getNutrientCategories));

  nutrientCategories: NutrientCategory[] = [{ key: 'general', ordinal: 0, nutrients: [] }];
  selectedNutrientCategory: NutrientCategory;
  labResults: LabResults;
  dataSource: MatTableDataSource<LabResultTableItem>;
  paginatorSettings: { length: number; pageIndex: number; pageSize: number };
  columndefs: string[] = [];
  allDisplayedColumns: string[] = [];
  pageNumber = 1;
  pageSize = 25;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.appStore.dispatch(
      SiteActions.updatePageTitle({ pageTitle: this.localeService.translate('title') }),
    );

    this.updateSelectedNutrientCategory(0);
    this.loadLabResults();

    this.nutrientCategories$
      .pipe(
        filter((nutrientCategories) => !!nutrientCategories),
        takeUntil(this.destroy$),
      )
      .subscribe((nutrientCategories) => {
        this.nutrientCategories = [this.nutrientCategories[0], ...nutrientCategories];

        this.updateTable();
      });

    this.labResults$
      .pipe(
        filter((labResults) => !!labResults),
        takeUntil(this.destroy$),
      )
      .subscribe((labResults) => {
        this.labResults = labResults;
        this.paginatorSettings = {
          length: labResults.pagination.totalNumberOfItems,
          pageIndex: labResults.pagination.pageNumber - 1,
          pageSize: labResults.pagination.pageSize,
        };

        this.updateTable();
      });
  }

  loadLabResults() {
    this.overviewStore.dispatch(
      OverviewActions.loadLabResults({
        keyword: this.form.controls.search.value,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
      }),
    );
  }

  reloadLabResults(pageEvent: PageEvent) {
    this.pageNumber = pageEvent.pageIndex + 1;
    this.pageSize = pageEvent.pageSize;
    this.loadLabResults();
  }

  searchLabResults() {
    this.pageNumber = 1;
    this.loadLabResults();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateSelectedNutrientCategory(newTabIndex: number): void {
    this.columndefs = [
      'descriptionAndId',
      'samplingDate',
      'receiptDate',
      'cropMowingDate',
      'material',
      'statusDescription',
      'labId',
      'hasReport',
    ];
    if (newTabIndex > 0) {
      this.columndefs = ['descriptionAndId'];
    }

    this.selectedNutrientCategory = this.nutrientCategories[newTabIndex];
    this.updateTable();
  }

  clearSearchbox() {
    this.form.controls.search.setValue('');
    this.loadLabResults();
  }

  downloadReport(id: number): void {
    this.overviewStore.dispatch(OverviewActions.downloadReport({ id }));
  }

  private updateTable(): void {
    if (!!this.labResults && !!this.selectedNutrientCategory) {
      this.allDisplayedColumns = [
        ...this.columndefs,
        ...this.selectedNutrientCategory.nutrients.map((nutrient) => nutrient.id.toString()),
      ];

      const tableItems = this.labResults.labResults.map((labResult) => ({
        id: labResult.id,
        labResultId: labResult.labResultId,
        description: labResult.description,
        statusDescription: `labresults.status.${labResult.statusDescription}`,
        labId: labResult.labId.charAt(0).toUpperCase() + labResult.labId.slice(1).toLowerCase(),
        reason: labResult.reason,
        receiptDate:
          labResult.receiptDateDateTime?.length > 0
            ? new Date(labResult.receiptDateDateTime).toLocaleString().split(' ')[0]
            : '',
        cropMowingDate:
          labResult.cropMowingDateTime?.length > 0
            ? new Date(labResult.cropMowingDateTime).toLocaleString().split(' ')[0]
            : '',
        samplingDate:
          labResult.samplingDateTime?.length > 0
            ? new Date(labResult.samplingDateTime).toLocaleString().split(' ')[0]
            : '',
        material: labResult.material,
        nutrients: this.selectedNutrientCategory.nutrients.map((nutrient) => ({
          id: nutrient.id,
          value: labResult.calculatedNutrients.find(
            (labresultNutrient) => labresultNutrient.id === nutrient.id,
          )?.value,
          format: `1.0-${nutrient.precision || 0}`,
        })),
        hasReport: labResult.hasReport,
      }));
      this.dataSource = new MatTableDataSource(tableItems);
    }
  }
}

export interface LabResultTableItem {
  id: number;
  labResultId: string;
  description: string;
  statusDescription: string;
  labId: string;
  reason: string;
  receiptDate: string;
  cropMowingDate: string;
  samplingDate: string;
  material: string;
  nutrients: LabResultNutrientTableItem[];
  hasReport: boolean;
}

export interface LabResultNutrientTableItem {
  id: number;
  value: number;
  format: string;
}
